import { checkVersionForward } from "@/apis/dsd/route";
import { ReportPeriod, ReportType } from "@/components/common/report-chip";
import { FileDropArea } from "@/components/file-drop-zone";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import {
  parseDsdFilename,
  ParsedDsdMeta,
} from "@/components/modals/upload-dsd/parse-dsd-filename";
import { pushUploadingModal } from "@/components/modals/upload-dsd/uploading-phase";
import { popModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import { ArrowDown, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { NewFileCard } from "./new-file-card";
import { SelectedReportCard } from "./selected-report-card";

export interface VersionUpdateTarget {
  id: string;
  name: string;
  type?: ReportType;
  period?: ReportPeriod;
  /** 현재 최신 버전 (예: "V14") */
  currentVersion: string;
}

interface VersionUpdateModalProps {
  report: VersionUpdateTarget;
}

const DSD_EXTENSION = ".dsd";

// ============================================
// VersionUpdateModal
// 선택된 보고서에 새 DSD 파일을 올려 다음 버전으로 업데이트한다.
// 상태: 파일 선택 전(업로드 영역) → 확인 중 → 완료(영역 안 파일 카드) / 오류(영역 아래 안내).
//
// 파일을 고르면 업로드 모달과 같은 version-forward/check 를 태운다. 새 파일의 유형·기간을
// 파일 내용에서 받아오기 위한 것 — 안 태우면 새 버전 카드가 "기존 보고서"의 뱃지를 자기
// 것처럼 보여줘, 다른 기간 파일을 올려도 이전 기간으로 표시된다. 파일이 깨졌으면 여기서
// 실패하므로 60초짜리 업로드를 태우기 전에 알 수 있다.
// ============================================

export const VersionUpdateModal = ({ report }: VersionUpdateModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    mutate: runCheck,
    data: checkData,
    isPending: isChecking,
  } = useMutation({
    mutationFn: (f: File) => checkVersionForward(f),
    // 화면 되돌리기는 mutate 쪽 onError 가 맡는다 — 여기 콜백은 뒤늦게 끝난 옛 요청에도 불려서
    // 이미 교체한 새 파일을 지워버린다. 리포팅은 그 경우에도 남겨야 하니 이 자리에 둔다.
    onError: (error, f) =>
      Sentry.captureException(error, {
        extra: { filename: f.name, fileSize: f.size },
      }),
  });

  // 새 파일 뱃지: 백엔드가 파일 내용으로 감지한 값 우선, 없으면 파일명 추정 폴백.
  const newMeta = useMemo<ParsedDsdMeta>(() => {
    const detected = checkData?.detected;
    if (detected) {
      return {
        year: detected.reportYear,
        type:
          detected.financialStatementType === "BOTH"
            ? undefined
            : detected.financialStatementType,
        period: detected.reportQuarter,
      };
    }
    return file ? parseDsdFilename(file.name) : {};
  }, [checkData, file]);

  const handleFile = (selected: File) => {
    setError(null);
    setFile(selected);
    // 확인 중에도 영역이 열려 있어 파일을 교체할 수 있다. mutate 에 넘긴 콜백은 마지막 요청에만
    // 불리므로, 앞선 요청이 뒤늦게 실패해도 방금 고른 파일을 지우지 않는다.
    runCheck(selected, {
      onError: () => {
        setFile(null);
        setError(
          "이 파일을 읽을 수 없습니다. DSD 파일이 맞는지 확인 후 다시 업로드해 주세요.",
        );
      },
    });
  };

  const removeFile = () => {
    setError(null);
    setFile(null);
  };

  // 업로드 확정 — 진행률 화면은 별도 모달로 교체한다. (업로드 모달과 같은 이유:
  // 선택 단계는 ESC 로 닫아도 되지만 업로드 중에는 닫히면 안 된다.)
  const startUpload = () => {
    if (!file) return;
    popModal();
    pushUploadingModal(file, report.id);
  };

  return (
    <ModalWrapper className="flex min-h-[548px] w-[600px] max-w-[600px] flex-col gap-0 overflow-hidden rounded-lg border border-line-normal bg-white p-0 shadow-xxlarge">
      <div className="flex items-center px-6 pt-5">
        <h2 className="headline-1-semibold text-label-normal">버전 업데이트</h2>
      </div>
      <div className="px-6 pb-3 pt-2">
        <p className="label-1-normal-regular text-label-alternative">
          선택된 보고서의 태깅 정보를 불러와 최신 버전으로 업데이트할 수
          있습니다.
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-2 px-6 pb-6 pt-2">
        <div className="flex w-full flex-col gap-2">
          <p className="label-2-regular text-label-normal">선택된 보고서</p>
          <SelectedReportCard
            name={report.name}
            type={report.type}
            period={report.period}
            currentVersion={report.currentVersion}
          />
        </div>

        <div className="flex w-full flex-col items-center gap-2">
          <ArrowDown className="size-6 text-label-normal" />
          <FileDropArea
            accept={DSD_EXTENSION}
            hint="*.dsd 형식"
            className="h-[210px]"
            compactWhenSelected
            error={error}
            selectedHint={
              file
                ? "다른 파일로 교체하려면 클릭하거나 드래그하세요"
                : undefined
            }
            onFilesSelect={(files) => handleFile(files[0])}
            onReject={({ kind, message }) => {
              // 오류 상태는 '파일 없음 + 오류' 하나로 둔다 — 앞서 고른 파일 카드가 남아 있으면
              // 업로드해도 되는지 헷갈린다.
              setFile(null);
              // 형식 안내만 이 모달 문구로 바꾼다 — 받는 건 .dsd 중에서도 감사/검토보고서다
              setError(
                kind === "format"
                  ? "지원하지 않는 파일 형식입니다. DSD(감사/검토)보고서 파일만 업로드 할 수 있습니다."
                  : message,
              );
            }}
          >
            {file && (
              <div className="flex flex-col gap-2">
                <div className="h-px w-full bg-line-normal" />
                {isChecking ? (
                  <div className="flex h-[72px] items-center justify-center gap-2 caption-1-medium text-label-alternative">
                    <Loader2 className="size-5 animate-spin" />
                    파일을 확인하는 중...
                  </div>
                ) : (
                  <NewFileCard
                    filename={file.name}
                    meta={newMeta}
                    onRemove={removeFile}
                  />
                )}
              </div>
            )}
          </FileDropArea>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 px-6 pb-5">
        <Button variant="outlined" size="base" onClick={popModal}>
          취소
        </Button>
        <Button
          variant="solid"
          color="primary"
          size="base"
          disabled={!file || isChecking}
          onClick={startUpload}
        >
          버전 업데이트
        </Button>
      </div>
    </ModalWrapper>
  );
};
